import React, { useState, useEffect } from 'react';
import { getAuthToken, getAuthHeaders } from '../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProjectsDashboard = ({ onSelectProject, onNewProject, onBack }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/projects/user/list`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      } else {
        setError('Erreur lors du chargement des projets');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId) => {
    try {
      setDeleting(true);
      
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setProjects(projects.filter(p => p.id !== projectId));
        setDeleteConfirm(null);
      } else {
        setError('Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'preview': { label: 'Aperçu', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      'generating': { label: 'En cours', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      'complete': { label: 'Complet', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      'error': { label: 'Erreur', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
    };
    const config = statusMap[status] || statusMap['preview'];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getBusinessTypeIcon = (type) => {
    const icons = {
      'artisan': '🔨',
      'coach': '🎯',
      'consultant': '💼',
      'ecommerce': '🛒',
      'restaurant': '🍽️',
      'immobilier': '🏠',
      'freelance': '💻',
      'startup': '🚀',
      'saas': '☁️',
      'formation': '📚',
      'therapeute': '🧘',
      'avocat': '⚖️',
      'comptable': '📊',
      'autre': '📌'
    };
    return icons[type] || '📌';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement de vos projets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-[#0a0a0f]/95 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={onBack}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
                data-testid="dashboard-back-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Mes Projets Marketing</h1>
                <p className="text-sm text-gray-400">{projects.length} projet{projects.length > 1 ? 's' : ''}</p>
              </div>
            </div>
            
            <button
              onClick={onNewProject}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg font-medium text-sm transition-all flex items-center justify-center space-x-2"
              data-testid="new-project-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
              </svg>
              <span>Nouvelle Stratégie</span>
            </button>
          </div>
        </div>
      </header>

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl flex items-center space-x-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
            </svg>
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-400/70 hover:text-red-400">×</button>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {projects.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2">Aucun projet pour l'instant</h2>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Créez votre première stratégie marketing IA en quelques minutes et découvrez la puissance de VF ACTIONS.
            </p>
            <button
              onClick={onNewProject}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-medium transition-all inline-flex items-center space-x-2"
              data-testid="empty-new-project-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
              </svg>
              <span>Créer ma première stratégie</span>
            </button>
          </div>
        ) : (
          /* Projects Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                className="bg-gray-900/50 border border-gray-800 rounded-2xl p-5 hover:border-gray-700 transition-all group"
                data-testid={`project-card-${project.id}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center text-xl">
                      {getBusinessTypeIcon(project.business_type)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white truncate max-w-[180px]" title={project.name}>
                        {project.name || 'Sans nom'}
                      </h3>
                      <p className="text-xs text-gray-500 capitalize">{project.business_type}</p>
                    </div>
                  </div>
                  {getStatusBadge(project.status)}
                </div>

                {/* Description */}
                {project.business_description && (
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                    {project.business_description}{project.business_description.length >= 95 ? '...' : ''}
                  </p>
                )}

                {/* Tagline */}
                {project.tagline && (
                  <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <p className="text-sm text-green-400 italic">"{project.tagline}"</p>
                  </div>
                )}

                {/* Progress Indicators */}
                <div className="flex items-center space-x-2 mb-4">
                  <div className={`w-2 h-2 rounded-full ${project.has_avatar ? 'bg-green-500' : 'bg-gray-600'}`} title="Avatar"></div>
                  <div className={`w-2 h-2 rounded-full ${project.has_positioning ? 'bg-green-500' : 'bg-gray-600'}`} title="Positionnement"></div>
                  <div className={`w-2 h-2 rounded-full ${project.has_offer ? 'bg-green-500' : 'bg-gray-600'}`} title="Offre"></div>
                  <div className={`w-2 h-2 rounded-full ${project.has_content ? 'bg-green-500' : 'bg-gray-600'}`} title="Contenus"></div>
                  {project.content_count > 0 && (
                    <span className="text-xs text-gray-500 ml-2">
                      {project.content_count} contenus
                    </span>
                  )}
                </div>

                {/* Date */}
                <p className="text-xs text-gray-500 mb-4">
                  Mis à jour le {formatDate(project.updated_at)}
                </p>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onSelectProject(project.id)}
                    className="flex-1 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg font-medium text-sm transition-all flex items-center justify-center space-x-2"
                    data-testid={`view-project-${project.id}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                    <span>Voir</span>
                  </button>
                  
                  <button
                    onClick={() => setDeleteConfirm(project.id)}
                    className="p-2 hover:bg-red-500/20 text-gray-400 hover:text-red-400 rounded-lg transition-all"
                    title="Supprimer"
                    data-testid={`delete-project-${project.id}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="delete-modal">
          <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full border border-gray-700">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold">Supprimer ce projet ?</h3>
            </div>
            
            <p className="text-gray-400 mb-6">
              Cette action est irréversible. Tous les contenus marketing générés seront perdus.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Suppression...</span>
                  </>
                ) : (
                  <span>Supprimer</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsDashboard;
